import { useNavigate } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import {
  Frown,
  BarChart3,
  MessageSquareOff,
  Check,
  Star,
  ChevronRight,
  Zap,
  Users,
  DollarSign,
  Shield,
  GraduationCap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import logo from "@/assets/logo.png";
import FeatureShowcase from "@/components/landing/FeatureShowcase";
import CommunitySection from "@/components/landing/CommunitySection";

/* ───── animation helpers ───── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const staggerSlow = {
  visible: { transition: { staggerChildren: 0.2 } },
};

function Section({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={stagger}
      className={className}
    >
      {children}
    </motion.section>
  );
}

/* ───── animated counter ───── */
function AnimatedCounter({ target, duration = 1.5 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, target, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setCount(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, target, duration]);

  return <span ref={ref}>{count.toLocaleString()}+</span>;
}

/* ───── data ───── */
const painPoints = [
  { icon: Frown, title: "Playing on Feel", desc: "You're making $500 decisions based on gut instinct instead of math. Every session is a coin flip." },
  { icon: BarChart3, title: "No Data, No Edge", desc: "You have no idea if you're winning or bleeding — no session logs, no trends, no bankroll tracking." },
  { icon: MessageSquareOff, title: "Zero Feedback Loop", desc: "You replay hands in your head at 3 AM. No coach. No AI. No one telling you where you're leaking." },
];

const testimonials = [
  { name: "Jake R.", role: "$2/5 NLH Regular", quote: "I went from break-even to consistently profitable within two months. The AI analysis showed me leaks I didn't even know I had.", rating: 5 },
  { name: "Maria S.", role: "Tournament Grinder", quote: "The voice logging is a game-changer. I record hands live and review them the next morning with the AI coach. My ROI is up 30%.", rating: 5 },
  { name: "Coach Dave", role: "Poker Coach", quote: "I onboarded 12 new students through Apex in the first month. The platform handles bookings, payments, everything.", rating: 5 },
];

const faqs = [
  { q: "Is Apex GTO really free to start?", a: "Yes. Create your account, log sessions, track stats, and use the AI coach — all free. Premium features like advanced analytics and unlimited AI analysis are available with a subscription." },
  { q: "What stakes and game types does it support?", a: "All of them. Cash games, tournaments, sit & gos — from micro-stakes to high-roller. You can even add custom stakes and locations." },
  { q: "How does the AI hand analysis work?", a: "You log your hand (manually or by voice), and our AI engine compares your actions against GTO-optimal play. It highlights deviations, rates your decisions, and explains what you should have done differently." },
  { q: "Can I use it at the table?", a: "Absolutely. The voice logging feature lets you record hands in real-time without touching your phone. Review and analyze later." },
  { q: "I'm a coach — how do I get listed?", a: "Sign up, choose 'I'm a Coach,' set your rates and availability, and you're live. Students can discover you, book sessions, and leave reviews — all inside the platform." },
];

/* ───── component ───── */
const Landing = () => {
  const navigate = useNavigate();
  const goAuth = () => navigate("/auth");

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* ── NAV ── */}
      <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-border/40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Apex GTO" className="h-8 w-8" width={32} height={32} decoding="async" />
            <span className="font-display text-lg font-bold uppercase tracking-wider text-foreground">
              Apex <span className="text-primary">GTO</span>
            </span>
          </div>
          <Button size="sm" onClick={goAuth}>
            Sign In
          </Button>
        </div>
      </nav>

      <main>
      <header className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Animated gradient background — CSS-only to avoid forced reflow */}
        <div className="absolute inset-0 pointer-events-none hero-gradient-bg" />
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-primary font-display text-sm uppercase tracking-[0.25em] mb-4"
          >
            AI-Powered Poker Performance Lab
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase leading-tight tracking-tight"
          >
            Stop Leaving <span className="text-gradient-gold">Money</span> on the Table
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
          >
            Most players guess. You'll know. Apex GTO gives you AI-driven hand analysis, real-time session tracking, and a coaching marketplace — so every decision is backed by data.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              size="lg"
              className="text-base px-8 py-6 font-display uppercase tracking-wider animate-glow-pulse"
              onClick={goAuth}
            >
              Start Winning More — Free <ChevronRight className="ml-1 h-5 w-5" />
            </Button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-4 text-xs text-muted-foreground"
          >
            Join <AnimatedCounter target={1000} /> players leveling up their game
          </motion.p>
        </div>
      </header>

      {/* ── PROBLEM / AGITATION ── */}
      <Section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.h2 variants={fadeUp} className="font-display text-2xl sm:text-3xl font-bold uppercase text-center tracking-wide mb-4">
            Sound Familiar?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            If you're serious about poker but still flying blind, you're not alone — but you are losing.
          </motion.p>
          <div className="grid md:grid-cols-3 gap-6">
            {painPoints.map((p) => (
              <motion.div key={p.title} variants={fadeUp}>
                <motion.div
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <Card className="bg-card border-border/50 h-full">
                    <CardContent className="p-6 space-y-3">
                      <motion.div whileHover={{ rotate: [0, -10, 10, -10, 0] }} transition={{ duration: 0.4 }}>
                        <p.icon className="h-10 w-10 text-destructive" />
                      </motion.div>
                      <h3 className="font-display text-lg font-semibold uppercase tracking-wider">{p.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── SOLUTION INTRO ── */}
      <Section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div variants={fadeUp} className="flex justify-center mb-6">
            <motion.img
              src={logo}
              alt="Apex GTO"
              className="h-16 w-16 drop-shadow-lg"
              width={64}
              height={64}
              loading="lazy"
              decoding="async"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-wide mb-4">
            Introducing <span className="text-gradient-gold">Apex GTO</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
            Your all-in-one AI performance lab. Track every session, analyze every hand, scout every opponent, and learn from top coaches — all in one place.
          </motion.p>
        </div>
      </Section>

      {/* ── FEATURE SHOWCASE ── */}
      <FeatureShowcase />

      {/* ── COMMUNITY ── */}
      <CommunitySection />

      {/* ── TESTIMONIALS ── */}
      <Section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <motion.h2 variants={fadeUp} className="font-display text-2xl sm:text-3xl font-bold uppercase text-center tracking-wide mb-12">
            What Players Are Saying
          </motion.h2>
          <motion.div variants={staggerSlow} className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <motion.div key={t.name} variants={fadeUp}>
                <motion.div
                  whileHover={{ y: -6, boxShadow: "0 16px 32px rgba(0,0,0,0.25)" }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <Card className="bg-card border-border/50 h-full">
                    <CardContent className="p-6 space-y-4">
                      <div className="flex gap-0.5">
                        {Array.from({ length: t.rating }, (_, i) => (
                          <motion.div
                            key={i}
                            initial={{ scale: 0, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.2 + i * 0.08, type: "spring", stiffness: 400 }}
                          >
                            <Star className="h-4 w-4 fill-primary text-primary" />
                          </motion.div>
                        ))}
                      </div>
                      <p className="text-sm text-foreground leading-relaxed italic">"{t.quote}"</p>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.role}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* ── FOR COACHES ── */}
      <Section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeUp}
            className="card-elevated rounded-2xl p-8 sm:p-12 text-center border border-border/50 animate-border-glow"
          >
            <motion.div variants={fadeUp} className="flex justify-center mb-4">
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <GraduationCap className="h-12 w-12 text-primary" />
              </motion.div>
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-wide mb-4">
              Are You a Coach?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
              List your profile, set your rates, and let students find you. Apex handles bookings, payments, and reviews — you just coach.
            </motion.p>
            <motion.div variants={fadeUp} className="grid sm:grid-cols-3 gap-6 mb-8">
              {[
                { icon: Users, text: "Get discovered by serious students" },
                { icon: DollarSign, text: "Get paid — we handle the billing" },
                { icon: Shield, text: "Build your reputation with reviews" },
              ].map((item) => (
                <motion.div
                  key={item.text}
                  className="flex flex-col items-center gap-2"
                  whileHover={{ scale: 1.08 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <item.icon className="h-8 w-8 text-primary/80" />
                  <p className="text-sm text-muted-foreground">{item.text}</p>
                </motion.div>
              ))}
            </motion.div>
            <motion.div variants={fadeUp}>
              <Button size="lg" variant="outline" className="font-display uppercase tracking-wider" onClick={goAuth}>
                Apply as a Coach <ChevronRight className="ml-1 h-5 w-5" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </Section>

      {/* ── PRICING / CTA ── */}
      <Section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div variants={fadeUp} className="flex justify-center mb-4">
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Zap className="h-10 w-10 text-primary" />
            </motion.div>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-wide mb-4">
            Start Free. Upgrade When You're Ready.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
            Create your account, log sessions, analyze hands, and find games — all completely free. Unlock premium AI analysis and advanced stats with a subscription.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col items-center gap-3">
            <Button
              size="lg"
              className="text-base px-10 py-6 font-display uppercase tracking-wider animate-glow-pulse"
              onClick={goAuth}
            >
              Start Your Free Account <ChevronRight className="ml-1 h-5 w-5" />
            </Button>
            <p className="text-xs text-muted-foreground">No credit card required</p>
          </motion.div>
        </div>
      </Section>

      {/* ── FAQ ── */}
      <Section className="py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.h2 variants={fadeUp} className="font-display text-2xl sm:text-3xl font-bold uppercase text-center tracking-wide mb-12">
            Frequently Asked Questions
          </motion.h2>
          <motion.div variants={fadeUp}>
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border/50 rounded-lg px-4 bg-card">
                  <AccordionTrigger className="text-sm font-semibold text-foreground hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </Section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border/40 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Apex GTO" className="h-6 w-6" />
            <span className="font-display text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Apex GTO
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <button onClick={goAuth} className="hover:text-primary transition-colors">
              Sign In
            </button>
            <span>© {new Date().getFullYear()} Apex GTO</span>
          </div>
        </div>
      </footer>
      </main>
    </div>
  );
};

export default Landing;
